import type { VersionedJson } from '@builder4/shared/migrations/types';

const HEADING_LEVELS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] as const;
const BUTTON_KEYS = ['button1', 'button2', 'button3'] as const;
const LINK_KEYS = ['link1', 'link2'] as const;
const BUTTON_NULLABLE_FIELDS = [
  'backgroundColor',
  'borderColor',
  'backgroundHoverColor',
  'borderHoverColor',
  'fontHoverColor',
] as const;

interface StyleNode {
  [key: string]: unknown;
}

interface DataWithStyles extends VersionedJson {
  typography?: {
    heading?: Record<string, StyleNode>;
    body?: StyleNode;
    link?: Record<string, StyleNode>;
    [key: string]: unknown;
  };
  buttons?: Record<string, StyleNode>;
}

function nullifyUndefinedFields(node: StyleNode, fields: readonly string[]): StyleNode {
  const result = { ...node };
  for (const field of fields) {
    if (result[field] === undefined) {
      result[field] = null;
    }
  }
  return result;
}

export default function toV3<T extends VersionedJson>(data: T): T {
  const typedData = data as DataWithStyles;
  const { typography, buttons } = typedData;

  let newTypography = typography;
  if (typography && typeof typography === 'object') {
    const heading = typography.heading;
    let newHeading = heading;
    if (heading && typeof heading === 'object') {
      newHeading = { ...heading };
      for (const level of HEADING_LEVELS) {
        if (heading[level] && typeof heading[level] === 'object') {
          newHeading[level] = nullifyUndefinedFields(heading[level], ['backgroundColor']);
        }
      }
    }

    const link = typography.link;
    let newLink = link;
    if (link && typeof link === 'object') {
      newLink = { ...link };
      for (const key of LINK_KEYS) {
        if (link[key] && typeof link[key] === 'object') {
          newLink[key] = nullifyUndefinedFields(link[key], ['backgroundColor']);
        }
      }
    }

    newTypography = {
      ...typography,
      ...(newHeading && { heading: newHeading }),
      ...(typography.body && {
        body: nullifyUndefinedFields(typography.body, ['backgroundColor']),
      }),
      ...(newLink && { link: newLink }),
    };
  }

  let newButtons = buttons;
  if (buttons && typeof buttons === 'object') {
    newButtons = { ...buttons };
    for (const btn of BUTTON_KEYS) {
      if (buttons[btn] && typeof buttons[btn] === 'object') {
        newButtons[btn] = nullifyUndefinedFields(buttons[btn], BUTTON_NULLABLE_FIELDS);
      }
    }
  }

  return {
    ...data,
    ...(newTypography && { typography: newTypography }),
    ...(newButtons && { buttons: newButtons }),
  } as T;
}
